import { useState, useEffect } from "react";
import { Download, Smartphone, Share2, MoreVertical, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Inline install card — drop anywhere on the page.
 * Props:
 *   - portalMode: if true, installs a shortcut to /cliniclogin
 *   - compact: render a single-row version (for smaller embed slots)
 */
export default function InstallAppInline({ portalMode = false, compact = false }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = typeof navigator !== "undefined" && /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;
  const isStandalone = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);
  // Don't offer the main-site install on the Companion surface — Companion
  // has its own install chip (`CompanionInstallChip`) that registers the
  // scoped /companion manifest so the home-screen icon opens directly into
  // the app. Two overlapping prompts confuses the browser's PWA promoter.
  const onCompanion = typeof window !== "undefined" && window.location.pathname.indexOf("/companion") === 0;
  const targetUrl = typeof window !== "undefined" ? (portalMode ? `${window.location.origin}/cliniclogin` : window.location.origin) : "";
  // Heading copy spells out exactly which PWA you're installing — the
  // main marketing/login site, NOT the Companion app. The Companion has
  // its own install path at /companion/install.
  const appName = portalMode ? "GLPFlo Staff Portal" : "GLPFlo Main Site";

  useEffect(() => {
    if (isStandalone) return;
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, [isStandalone]);

  if (isStandalone) return null;
  if (onCompanion) return null;

  const downloadShortcut = () => {
    const content = `[InternetShortcut]\nURL=${targetUrl}\nIconIndex=0\n`;
    const blob = new Blob([content], { type: "application/internet-shortcut" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${appName}.url`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return;
    }
    if (isIOS || (isMobile && !deferredPrompt)) { setShowGuide(true); return; }
    if (!isMobile) { downloadShortcut(); return; }
  };

  const buttonLabel = deferredPrompt
    ? `Install ${appName} App`
    : isIOS ? "Save to Home Screen"
    : isMobile ? "Install on Phone"
    : "Save to Desktop";

  if (compact) {
    const compactHeading = isIOS ? "Add to Home Screen"
      : isAndroid ? "Install App"
      : "Save shortcut";
    return (
      <>
        <div className="rounded-2xl bg-white border border-purple-100 p-3 flex items-center gap-3 shadow-sm" data-testid="install-inline-compact">
          <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{compactHeading}</p>
            <p className="text-xs text-slate-500 truncate">{isMobile ? "GLPFlo · one-tap access" : "GLPFlo · quick access"}</p>
          </div>
          <Button onClick={handleInstall} size="sm" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white p-2 h-8 w-8 flex-shrink-0" aria-label="Install" data-testid="install-inline-btn">
            <Download className="w-3.5 h-3.5" />
          </Button>
        </div>
        {showGuide && <GuideModal isIOS={isIOS} appName={appName} onClose={() => setShowGuide(false)} />}
      </>
    );
  }

  return (
    <>
      <div className="rounded-2xl bg-gradient-to-br from-purple-600 via-purple-700 to-violet-800 text-white p-6 shadow-lg shadow-purple-200 relative overflow-hidden" data-testid="install-inline">
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-white/5 blur-2xl" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-base" style={{ fontFamily: "'Outfit', sans-serif" }}>Install {appName}</p>
              <p className="text-xs text-purple-100">{portalMode ? "Quick staff dashboard access" : "Marketing & login. Looking for the daily logging app? Install the Companion separately."}</p>
            </div>
          </div>
          <Button onClick={handleInstall} className="w-full mt-1 bg-white text-purple-700 hover:bg-purple-50 rounded-full text-sm font-semibold" data-testid="install-inline-btn">
            <Download className="w-4 h-4 mr-2" /> {buttonLabel}
          </Button>
        </div>
      </div>
      {showGuide && <GuideModal isIOS={isIOS} appName={appName} onClose={() => setShowGuide(false)} />}
    </>
  );
}

function GuideModal({ isIOS, appName, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-sm w-full p-6 pb-8 sm:pb-6" onClick={(e) => e.stopPropagation()} data-testid="install-guide">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-purple-600 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-base text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{isIOS ? "Save to Home Screen" : `Install ${appName}`}</h3>
              <p className="text-xs text-slate-500">Quick access like a native app</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200"><X className="w-4 h-4" /></button>
        </div>
        {isIOS ? (
          <div className="space-y-4 text-sm text-slate-700">
            <Step n={1}><>Tap the <strong>Share</strong> button in Safari <span className="inline-flex items-center gap-1 bg-slate-100 rounded px-2 py-0.5"><Share2 className="w-3 h-3 text-blue-500" /> bottom of screen</span></></Step>
            <Step n={2}><>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></></Step>
            <Step n={3}><>Tap <strong>&quot;Add&quot;</strong> — Done!</></Step>
          </div>
        ) : (
          <div className="space-y-4 text-sm text-slate-700">
            <Step n={1}><>Tap the <strong>menu</strong> in Chrome <span className="inline-flex items-center gap-1 bg-slate-100 rounded px-2 py-0.5"><MoreVertical className="w-3 h-3 text-slate-600" /> top right</span></></Step>
            <Step n={2}><>Tap <strong>&quot;Add to Home screen&quot;</strong> or <strong>&quot;Install app&quot;</strong></></Step>
            <Step n={3}><>Tap <strong>&quot;Install&quot;</strong></></Step>
          </div>
        )}
        <Button onClick={onClose} className="w-full mt-5 rounded-full bg-purple-600 hover:bg-purple-700 text-white">Got it!</Button>
      </div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-7 h-7 rounded-full bg-purple-100 text-purple-600 text-xs font-bold flex items-center justify-center flex-shrink-0">{n}</span>
      <p>{children}</p>
    </div>
  );
}
